import React, { useRef, useState } from 'react'
import { AgTechRendering } from '@agtech/core/components/AgTechRendering';
import { AgTechCoreDataActionProps, AgTechDataAction } from '@agtech/core/data/actions/AgTechDataActions';
import { AgTechConfirmationProps, DefaultAgTechConfirmationActions, IAgTechAppConfirmations } from '@agtech/core/functions/AgTechAppConfirmations';
import AgTechConfirmationModal, { AgTechConfirmationModalProps, showConfirmationModal } from '../../Portals/Modals/AgTechConfirmationModal';
import AgTechModal, { AgTechModalRenderProps, showStandardModal } from '../../Portals/Modals/AgTechModal';
import AgTechWarningModal, { AgTechWarningModalProps, AgTechWarningModalRenderProps, showWarningModal } from '../../Portals/Modals/AgTechWarningModal';
import { v4 as uuidv4 } from 'uuid';
import { AgTechLoader } from '../../Pages/Loading/AgTechLoadingSurface';
import { AgTechActionModal, AgTechActionModalProps, showActionModal } from '../../Portals/Modals/AgTechActionModal';
import { logDev } from '@agtech/core/logging/AgTechLogger';
import { AgTechStepModalProps, showStepModal } from '../../Portals/Modals/AgTechStepModal';

/// Components

export type AgTechModalSurfaceProps = {
    modalManager: AgTechModalManager
}

let AgTechModalSurface = (props: AgTechModalSurfaceProps) => {
    return props.modalManager.modals ? (
        <>
            {props.modalManager.modals.map(modal => modal ? (
                <MemoModal key={modal.id} modal={modal} />
            ) : null)}
        </>
    ) : null;
}

const Modal = (props: { modal: AgTechRendering }) => {
    return (
        <>
            { props.modal.rendering() }
        </>
    )
}

const MemoModal = React.memo(Modal, (prev, curr) => true);

/// Manager

declare type AgTechModalRendering = {
    id: string,
    rendering: () => React.ReactNode
}

export type AgTechModalManager = IAgTechAppConfirmations & {
    modals: AgTechModalRendering[],
    showModal: (modal: AgTechModalRenderProps) => string,
    showActionModal: <TInput, TOutput, TContextData>(props: Omit<AgTechActionModalProps<TInput, TOutput, TContextData>, 'closeModal'>) => void,
    showWarningModal: (modal: AgTechWarningModalRenderProps) => Promise<void>,
    showLoader: (loadingMessage: string) => string,
    showStepModal: (modal: AgTechStepModalProps) => Promise<void>,
    closeModal: (modalId: string) => void
}

export const DefaultAgTechModalManager: AgTechModalManager = {
    modals: [],
    showModal: () => {
        console.error('Tried to show modal with default app context.');
        return '';
    },
    askForConfirmation: () => new Promise<boolean>(function(resolve, reject) {
        console.error('Tried to show confirmation modal with default app context');
        resolve(false);
    }),
    showActionModal: async () => {
        console.error('Tried to show action modal with default app context');
    },
    showWarningModal: () => new Promise<void>((res, rej) => {
        console.error('Tried to show warning modal with default app context');
        res();
    }),
    showStepModal: () => new Promise<void>((res, rej) => {
        console.error('Tried to show step modal with default app context');
        res();
    }),
    showLoader: () => {
        console.error('Tried to show loader modal with default app context.');
        return '';
    },
    closeModal: () => {
        console.error('Tried to close modal with default app context.');
    }
}

export const useModals = (): AgTechModalManager => {
    const [activeModalsState, updatedActiveModals] = useState<AgTechModalRendering[]>([]);
    const activeModalsRef = useRef<AgTechModalRendering[]>(activeModalsState);

    const showModal = (modalRenderProp: (modalId: string) => React.ReactNode) => {
        const modalId = uuidv4();

        const modalRendering: AgTechModalRendering = {
            id: modalId,
            rendering: () => modalRenderProp(modalId)
        };

        activeModalsRef.current = [...activeModalsRef.current, { ...modalRendering }];
        updatedActiveModals(activeModalsRef.current);

        return modalId;
    };

    const closeModal = (modalId: string) => {
        activeModalsRef.current = [...activeModalsRef.current.filter(modal => modal.id !== modalId)];
        updatedActiveModals(activeModalsRef.current);
    }

    const modalContext = {
        showModal: showModal,
        closeModal: closeModal
    }
    
    return {
        modals: activeModalsRef.current,
        showModal: (props) => showStandardModal(props, modalContext),
        showLoader: message => showStandardModal({
            content: () => (
                <AgTechLoader text={message} />
            ),
            classes: 'p-8',
            showCloseButton: false
        }, modalContext),
        showActionModal: (props) => showActionModal(props, modalContext),
        askForConfirmation: (props) => showConfirmationModal(props, modalContext),
        showWarningModal: (props) => showWarningModal(props, modalContext),
        showStepModal: (props) => showStepModal(props, modalContext),
        closeModal: closeModal
    };
}

export default AgTechModalSurface;